/**
 * Selected card state for landing page card decks; resets when section scrolls away.
 */

import { useCallback, useEffect, useState, type RefObject } from 'react';

export function useLandingExpandableCards<T extends string>(sectionRef?: RefObject<HTMLElement | null>) {
  const [expandedKey, setExpandedKey] = useState<T | null>(null);

  const toggle = useCallback((key: T) => {
    setExpandedKey((current) => (current === key ? null : key));
  }, []);

  const collapse = useCallback(() => {
    setExpandedKey(null);
  }, []);

  useEffect(() => {
    if (expandedKey === null || !sectionRef?.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0] && !entries[0].isIntersecting) {
          setExpandedKey(null);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [expandedKey, sectionRef]);

  return {
    expandedKey,
    toggle,
    collapse,
    hasExpanded: expandedKey !== null,
  };
}
